/**
 * @file Components/Sections/CategoryShowcase.jsx
 * @description Homepage category quick-nav section with animated cards
 * linking to filtered tour listings. MakeMyTrip-style discovery UI.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Compass, GraduationCap, Mountain, Crown, Heart, Briefcase } from 'lucide-react';

const categories = [
  {
    key: 'customized',
    label: 'Customized',
    tagline: 'Your dream, our craft',
    icon: Compass,
    gradient: 'from-purple-500 to-pink-500',
    bg: 'bg-purple-50',
    image: 'https://images.unsplash.com/photo-1488085061387-422e29b40080?w=400&q=80',
  },
  {
    key: 'educational',
    label: 'Educational',
    tagline: 'Learn while you travel',
    icon: GraduationCap,
    gradient: 'from-green-500 to-teal-500',
    bg: 'bg-green-50',
    image: 'https://images.unsplash.com/photo-1523050854058-8df90110c8f1?w=400&q=80',
  },
  {
    key: 'adventurous',
    label: 'Adventurous',
    tagline: 'Push your limits',
    icon: Mountain,
    gradient: 'from-orange-500 to-red-500',
    bg: 'bg-orange-50',
    image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=400&q=80',
  },
  {
    key: 'luxury',
    label: 'Luxury',
    tagline: 'Travel in grandeur',
    icon: Crown,
    gradient: 'from-amber-500 to-yellow-500',
    bg: 'bg-amber-50',
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80',
  },
  {
    key: 'family',
    label: 'Family',
    tagline: 'Memories together',
    icon: Heart,
    gradient: 'from-pink-500 to-rose-500',
    bg: 'bg-pink-50',
    image: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?w=400&q=80',
  },
  {
    key: 'corporate',
    label: 'Corporate',
    tagline: 'Team building retreats',
    icon: Briefcase,
    gradient: 'from-slate-500 to-gray-600',
    bg: 'bg-slate-50',
    image: 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=400&q=80',
  },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};
const item = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const CategoryShowcase = () => {
  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Explore by Category
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Choose the type of experience that speaks to you
          </p>
        </div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
        >
          {categories.map((cat) => {
            const Icon = cat.icon;
            return (
              <motion.div key={cat.key} variants={item}>
                <Link
                  to={`/tours?category=${cat.key}`}
                  className="group block relative h-48 rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                >
                  <img
                    src={cat.image}
                    alt={cat.label}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${cat.gradient} opacity-60 group-hover:opacity-75 transition-opacity`} />
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-3 text-center">
                    <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                      <Icon size={24} />
                    </div>
                    <h3 className="font-bold text-base">{cat.label}</h3>
                    <p className="text-white/80 text-xs mt-0.5">{cat.tagline}</p>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default CategoryShowcase;
